/**
 * communityService — REST helpers for the community (WhatsApp-group) feature.
 *
 * Each "community" the user sees = one backend Community + one default CommunityGroup
 * (named "General", is_default: true).  All message read/write goes through that group.
 *
 * Base path: /communities  (verifyAppUserOrToken — client or admin JWT)
 */

import axiosInstance from '../../../shared/services/axiosConfig';
export {
  emitTyping,
  emitStopTyping,
  joinCommunityRoom,
  joinGroupRoom,
  leaveGroupRoom,
} from './communitySocketService';
import { isAllowedDocumentFile } from '../utils/documentFileTypes';

// ---------------------------------------------------------------------------
// Types (mirrors backend models — only the fields the frontend needs)
// ---------------------------------------------------------------------------

export interface Community {
  community_id: string;
  name: string;
  slug: string;
  description?: string;
  avatar_url?: string;
  cover_url?: string;
  visibility: 'public' | 'private' | 'invite_only';
  status: 'active' | 'archived';
  settings?: {
    allow_member_group_create?: boolean;
    allow_member_invite?: boolean;
    default_post_policy?: 'all_members' | 'admins_only';
    profanity_filter?: boolean;
  };
  member_count: number;
  group_count: number;
  created_by_client_id: string;
  archived_at?: string;
  archived_by_client_id?: string;
  created_at: string;
  updated_at?: string;
  // Injected by listForClient
  my_role?: 'owner' | 'moderator' | 'member';
  // Injected by discoverPublic
  is_member?: boolean;
}

export interface CommunityGroup {
  group_id: string;
  community_id: string;
  name: string;
  description?: string;
  avatar_url?: string;
  visibility: 'public' | 'private';
  type: 'chat' | 'announcement';
  post_policy: 'all_members' | 'admins_only';
  is_default: boolean;
  is_active: boolean;
  member_count: number;
  last_message_seq: number;
  last_message_at?: string;
  last_message_preview?: string;
  created_by_client_id: string;
  created_at: string;
  // Injected by listForClient
  my_membership: { role: 'owner' | 'admin' | 'member'; unread: number } | null;
}

export interface CommunityMessageMedia {
  filename?: string;
  mime?: string;
  size_bytes?: number;
  blob_url?: string;
  blob_name?: string;
  width?: number;
  height?: number;
  duration_ms?: number;
  thumb_url?: string;
  page_count?: number;
  latitude?: number;
  longitude?: number;
  location_label?: string;
}

export interface CommunityMessageLinkPreview {
  url: string;
  title?: string;
  description?: string;
  image_url?: string;
  site_name?: string;
  fetched_at?: string;
}

export type CommunityMessageType =
  | 'text'
  | 'image'
  | 'video'
  | 'audio'
  | 'document'
  | 'location'
  | 'system_event'
  | 'deleted';

export type CommunityMediaMessageType = 'image' | 'video' | 'audio' | 'document';

export interface SystemEvent {
  kind:
    | 'member_joined'
    | 'member_left'
    | 'member_removed'
    | 'member_banned'
    | 'role_changed'
    | 'group_created'
    | 'group_renamed';
  actor_client_id?: string;
  target_client_id?: string;
  metadata?: Record<string, unknown>;
}

export interface CommunityMessageForwardedFrom {
  community_message_id: string;
  community_id: string;
  group_id: string;
  sender_name: string;
  content_snippet?: string;
  original_timestamp: string;
}

export interface CommunityMessageReplyPreview {
  community_message_id: string;
  sender_name: string;
  content_snippet?: string;
  message_type: CommunityMessageType;
}

export interface CommunityMessage {
  community_message_id: string;
  community_id: string;
  group_id: string;
  seq: number;
  sender_client_id?: string;
  sender_name_cached: string;
  sender_avatar_cached?: string;
  message_type: CommunityMessageType;
  content?: string;
  media?: CommunityMessageMedia;
  link_preview?: CommunityMessageLinkPreview;
  reply_to_message_id?: string;
  reply_preview?: CommunityMessageReplyPreview;
  mentions?: string[];
  system_event?: SystemEvent;
  forwarded_from?: CommunityMessageForwardedFrom;
  is_pinned?: boolean;
  pinned_at?: string;
  pinned_by_client_id?: string;
  edited_at?: string;
  deleted_at?: string;
  deleted: boolean;
  timestamp: string;
}

export interface CommunityMember {
  community_id: string;
  client_id: string;
  role: 'owner' | 'moderator' | 'member';
  status: 'active' | 'left' | 'removed' | 'banned';
  joined_at: string;
  display_name?: string;
  avatar_url?: string;
  display_name_override?: string;
}

export interface CommunityInvite {
  token: string;
  community_id: string;
  group_id?: string;
  type: 'member' | 'group_admin' | 'moderator';
  max_uses?: number;
  use_count: number;
  expires_at?: string;
  revoked_at?: string;
  created_by_client_id: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Community CRUD
// ---------------------------------------------------------------------------

export async function listMyCommunities(): Promise<{ communities: Community[]; total: number }> {
  const res = await axiosInstance.get<{ communities: Community[]; total: number }>(
    '/communities'
  );
  return res.data;
}

export async function listAdminCommunities(opts?: {
  search?: string;
  status?: 'active' | 'archived' | 'suspended';
  limit?: number;
  offset?: number;
}): Promise<{ communities: Community[]; total: number }> {
  const res = await axiosInstance.get<{ communities: Community[]; total: number }>(
    '/admin/communities',
    { params: opts }
  );
  return res.data;
}

export type BulkAddCommunityMembersResult = {
  community_id: string;
  added_count: number;
  already_member_count: number;
  failed_count: number;
  added: string[];
  already_member: string[];
  failed: Array<{ client_id: string; reason: string }>;
};

/** Admin: add selected clients to a community (bulk action from Clients page). */
export async function bulkAddClientsToCommunity(
  communityId: string,
  clientIds: string[]
): Promise<BulkAddCommunityMembersResult> {
  const res = await axiosInstance.post<BulkAddCommunityMembersResult>(
    `/admin/communities/${communityId}/members/bulk`,
    { client_ids: clientIds }
  );
  return res.data;
}

export async function listAdminGroups(communityId: string): Promise<CommunityGroup[]> {
  const res = await axiosInstance.get<{ groups: CommunityGroup[] }>(
    `/admin/communities/${communityId}/groups`
  );
  return res.data.groups ?? [];
}

export async function listAdminMessages(
  communityId: string,
  groupId: string,
  opts: { before_seq?: number; limit?: number } = {}
): Promise<{ messages: CommunityMessage[]; next_cursor: number | null; pinned_message?: CommunityMessage | null }> {
  const res = await axiosInstance.get<{
    messages: CommunityMessage[];
    next_cursor: number | null;
    pinned_message?: CommunityMessage | null;
  }>(`/admin/communities/${communityId}/groups/${groupId}/messages`, {
    params: { limit: opts.limit ?? 50, ...(opts.before_seq != null ? { before_seq: opts.before_seq } : {}) },
  });
  return {
    messages: [...res.data.messages].reverse(),
    next_cursor: res.data.next_cursor,
    pinned_message: res.data.pinned_message ?? null,
  };
}

export async function createCommunity(data: {
  name: string;
  description?: string;
  visibility?: 'public' | 'private' | 'invite_only';
}): Promise<Community> {
  const res = await axiosInstance.post<{ community: Community }>('/communities', data);
  return res.data.community;
}

export async function getCommunity(communityId: string): Promise<Community> {
  const res = await axiosInstance.get<{
    community: Community;
    my_membership: { role: CommunityMember['role']; joined_at: string } | null;
  }>(`/communities/${communityId}`);
  const { community, my_membership } = res.data;
  return {
    ...community,
    my_role: my_membership?.role,
    is_member: !!my_membership,
  };
}

export async function leaveCommunity(communityId: string): Promise<void> {
  await axiosInstance.post(`/communities/${communityId}/leave`);
}

export async function transferOwnership(
  communityId: string,
  newOwnerClientId: string
): Promise<void> {
  await axiosInstance.post(`/communities/${communityId}/transfer-ownership`, {
    new_owner_client_id: newOwnerClientId,
  });
}

export async function deleteCommunity(communityId: string): Promise<void> {
  await axiosInstance.delete(`/communities/${communityId}`);
}

export async function listCommunityMembers(
  communityId: string,
  opts: { cursor?: string; limit?: number } = {}
): Promise<{ members: CommunityMember[]; next_cursor: string | null }> {
  const res = await axiosInstance.get<{
    members: CommunityMember[];
    next_cursor: string | null;
  }>(`/communities/${communityId}/members`, { params: opts });
  return res.data;
}

export async function discoverPublicCommunities(
  opts: { search?: string; limit?: number; offset?: number } = {}
): Promise<{ communities: Community[]; total: number }> {
  const res = await axiosInstance.get<{ communities: Community[]; total: number }>(
    '/communities/discover',
    { params: opts }
  );
  return res.data;
}

export async function joinPublicCommunity(
  communityId: string
): Promise<{ community: Community; membership: CommunityMember }> {
  const res = await axiosInstance.post<{ community: Community; membership: CommunityMember }>(
    `/communities/${communityId}/join`
  );
  return res.data;
}

export async function updateCommunity(
  communityId: string,
  patch: {
    name?: string;
    description?: string;
    visibility?: 'public' | 'private' | 'invite_only';
    avatar_url?: string;
    cover_url?: string;
  }
): Promise<Community> {
  const res = await axiosInstance.patch<{ community: Community }>(
    `/communities/${communityId}`,
    patch
  );
  return res.data.community;
}

export async function uploadCommunityAvatar(
  communityId: string,
  file: File
): Promise<Community> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await axiosInstance.post<{ community: Community }>(
    `/communities/${communityId}/avatar`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return res.data.community;
}

export async function banCommunityMember(
  communityId: string,
  clientId: string,
  reason?: string
): Promise<void> {
  await axiosInstance.post(`/communities/${communityId}/members/${clientId}/ban`, {
    ...(reason ? { reason } : {}),
  });
}

/** Remove member without ban — they can rejoin with a new invite link. */
export async function removeCommunityMember(
  communityId: string,
  clientId: string
): Promise<void> {
  await axiosInstance.delete(`/communities/${communityId}/members/${clientId}`);
}

export async function setCommunityMemberRole(
  communityId: string,
  clientId: string,
  role: 'owner' | 'moderator' | 'member'
): Promise<void> {
  await axiosInstance.post(`/communities/${communityId}/members/${clientId}/role`, { role });
}

export async function listCommunityInvites(
  communityId: string,
  opts: { noCache?: boolean } = {}
): Promise<CommunityInvite[]> {
  const res = await axiosInstance.get<{ invites: CommunityInvite[] }>(
    `/communities/${communityId}/invites`,
    {
      params: opts.noCache ? { _: Date.now() } : undefined,
      headers: opts.noCache ? { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } : undefined,
    }
  );
  return res.data.invites;
}

export async function deleteCommunityInvite(
  communityId: string,
  token: string
): Promise<void> {
  await axiosInstance.delete(`/communities/${communityId}/invites/${token}`);
}

/** @deprecated Use deleteCommunityInvite */
export const revokeCommunityInvite = deleteCommunityInvite;

// ---------------------------------------------------------------------------
// Group CRUD
// ---------------------------------------------------------------------------

export async function createGroup(
  communityId: string,
  data: {
    name: string;
    description?: string;
    is_default?: boolean;
    type?: 'chat' | 'announcement';
    visibility?: 'public' | 'private';
  }
): Promise<CommunityGroup> {
  const res = await axiosInstance.post<{ group: CommunityGroup }>(
    `/communities/${communityId}/groups`,
    data
  );
  return res.data.group;
}

export async function listGroups(
  communityId: string,
  scope: 'mine' | 'public' = 'mine',
  opts: { noCache?: boolean } = {}
): Promise<CommunityGroup[]> {
  const res = await axiosInstance.get<{ groups: CommunityGroup[] }>(
    `/communities/${communityId}/groups`,
    {
      params: {
        scope,
        ...(opts.noCache ? { _: Date.now() } : {}),
      },
    }
  );
  return res.data.groups ?? [];
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export async function listMessages(
  communityId: string,
  groupId: string,
  opts: { before_seq?: number; limit?: number } = {}
): Promise<{ messages: CommunityMessage[]; next_cursor: number | null; pinned_message?: CommunityMessage | null }> {
  const res = await axiosInstance.get<{
    messages: CommunityMessage[];
    next_cursor: number | null;
    pinned_message?: CommunityMessage | null;
  }>(`/communities/${communityId}/groups/${groupId}/messages`, {
    params: { limit: opts.limit ?? 50, ...(opts.before_seq != null ? { before_seq: opts.before_seq } : {}) },
  });
  // Backend returns newest-first; reverse to chronological order for UI
  return {
    messages: [...res.data.messages].reverse(),
    next_cursor: res.data.next_cursor,
    pinned_message: res.data.pinned_message ?? null,
  };
}

export async function sendMessage(
  communityId: string,
  groupId: string,
  content: string,
  opts: { reply_to_message_id?: string } = {}
): Promise<CommunityMessage> {
  const res = await axiosInstance.post<{ message: CommunityMessage }>(
    `/communities/${communityId}/groups/${groupId}/messages`,
    {
      message_type: 'text',
      content,
      ...(opts.reply_to_message_id ? { reply_to_message_id: opts.reply_to_message_id } : {}),
    }
  );
  return res.data.message;
}

const MAX_MEDIA_BYTES = 100 * 1024 * 1024;

export function inferMediaMessageType(file: File): CommunityMediaMessageType {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'document';
}

export function validateMediaFile(
  file: File,
  opts: { asDocument?: boolean } = {}
): void {
  if (file.size > MAX_MEDIA_BYTES) {
    throw new Error('File size must be less than 100MB');
  }
  if (opts.asDocument && !isAllowedDocumentFile(file)) {
    throw new Error('This file type is not supported as a document');
  }
}

export async function getCommunityMediaUploadUrl(input: {
  group_id: string;
  message_type: CommunityMediaMessageType;
  filename: string;
  mime_type: string;
}): Promise<{ uploadUrl: string; blobUrl: string; blobName: string }> {
  const res = await axiosInstance.post<{ uploadUrl: string; blobUrl: string; blobName: string }>(
    '/communities/media/upload-url',
    input
  );
  return res.data;
}

export async function uploadFileToBlob(
  uploadUrl: string,
  file: Blob,
  mimeType: string
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': mimeType,
      'x-ms-blob-type': 'BlockBlob',
    },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Upload failed (${response.status})`);
  }
}

export async function uploadCommunityMediaViaApi(
  groupId: string,
  file: File | Blob,
  messageType: CommunityMediaMessageType,
  filename: string
): Promise<{ blobUrl: string; blobName: string; filename: string }> {
  const formData = new FormData();
  const uploadFile =
    file instanceof File ? file : new File([file], filename, { type: file.type || 'application/octet-stream' });
  formData.append('file', uploadFile);
  formData.append('group_id', groupId);
  formData.append('message_type', messageType);

  const res = await axiosInstance.post<{ blobUrl: string; blobName: string; filename: string }>(
    '/communities/media/upload',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return res.data;
}

async function uploadCommunityMediaFile(
  groupId: string,
  file: File | Blob,
  opts: {
    message_type: CommunityMediaMessageType;
    filename: string;
    mime_type: string;
  }
): Promise<{ blobUrl: string; blobName: string; filename: string }> {
  try {
    return await uploadCommunityMediaViaApi(groupId, file, opts.message_type, opts.filename);
  } catch {
    const { uploadUrl, blobUrl, blobName } = await getCommunityMediaUploadUrl({
      group_id: groupId,
      message_type: opts.message_type,
      filename: opts.filename,
      mime_type: opts.mime_type,
    });
    await uploadFileToBlob(uploadUrl, file, opts.mime_type);
    return { blobUrl, blobName, filename: opts.filename };
  }
}

/** Upload + send in one multipart request (most reliable on web). */
export async function sendCommunityMediaMultipart(
  communityId: string,
  groupId: string,
  file: File | Blob,
  opts: {
    message_type: CommunityMediaMessageType;
    filename: string;
    mime_type: string;
    content?: string;
    reply_to_message_id?: string;
    duration_ms?: number;
    thumb_url?: string;
    page_count?: number;
  }
): Promise<CommunityMessage> {
  const formData = new FormData();
  const uploadFile =
    file instanceof File ? file : new File([file], opts.filename, { type: opts.mime_type });
  formData.append('file', uploadFile);
  formData.append('message_type', opts.message_type);
  if (opts.content) formData.append('content', opts.content);
  if (opts.reply_to_message_id) formData.append('reply_to_message_id', opts.reply_to_message_id);
  if (opts.duration_ms != null) formData.append('duration_ms', String(opts.duration_ms));
  if (opts.thumb_url) formData.append('thumb_url', opts.thumb_url);
  if (opts.page_count != null) formData.append('page_count', String(opts.page_count));

  const res = await axiosInstance.post<{ message: CommunityMessage }>(
    `/communities/${communityId}/groups/${groupId}/messages`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return res.data.message;
}

export interface SendCommunityMessagePayload {
  message_type: CommunityMessageType;
  content?: string;
  media?: CommunityMessageMedia;
  reply_to_message_id?: string;
}

export async function sendCommunityMessage(
  communityId: string,
  groupId: string,
  payload: SendCommunityMessagePayload
): Promise<CommunityMessage> {
  const res = await axiosInstance.post<{ message: CommunityMessage }>(
    `/communities/${communityId}/groups/${groupId}/messages`,
    payload
  );
  return res.data.message;
}

export async function uploadAndSendMediaMessage(
  communityId: string,
  groupId: string,
  file: File | Blob,
  opts: {
    message_type: CommunityMediaMessageType;
    filename: string;
    mime_type: string;
    content?: string;
    reply_to_message_id?: string;
    duration_ms?: number;
    thumb_url?: string;
    page_count?: number;
  }
): Promise<CommunityMessage> {
  validateMediaFile(file instanceof File ? file : new File([file], opts.filename, { type: opts.mime_type }));

  try {
    return await sendCommunityMediaMultipart(communityId, groupId, file, opts);
  } catch {
    const { blobUrl, blobName, filename } = await uploadCommunityMediaFile(groupId, file, {
      message_type: opts.message_type,
      filename: opts.filename,
      mime_type: opts.mime_type,
    });

    return sendCommunityMessage(communityId, groupId, {
      message_type: opts.message_type,
      content: opts.content,
      reply_to_message_id: opts.reply_to_message_id,
      media: {
        filename,
        mime: opts.mime_type,
        size_bytes: file.size,
        blob_url: blobUrl,
        blob_name: blobName,
        ...(opts.duration_ms != null ? { duration_ms: opts.duration_ms } : {}),
        ...(opts.thumb_url ? { thumb_url: opts.thumb_url } : {}),
        ...(opts.page_count != null ? { page_count: opts.page_count } : {}),
      },
    });
  }
}

export async function uploadAndSendDocumentMessage(
  communityId: string,
  groupId: string,
  file: File,
  opts: {
    content?: string;
    reply_to_message_id?: string;
    thumbBlob?: Blob;
    pageCount?: number;
  } = {}
): Promise<CommunityMessage> {
  validateMediaFile(file, { asDocument: true });

  let thumbUrl: string | undefined;
  if (opts.thumbBlob) {
    const thumbFile = new File([opts.thumbBlob], `thumb-${Date.now()}.jpg`, {
      type: 'image/jpeg',
    });
    const uploaded = await uploadCommunityMediaViaApi(groupId, thumbFile, 'image', thumbFile.name);
    thumbUrl = uploaded.blobUrl;
  }

  return uploadAndSendMediaMessage(communityId, groupId, file, {
    message_type: 'document',
    filename: file.name,
    mime_type: file.type || 'application/octet-stream',
    content: opts.content,
    reply_to_message_id: opts.reply_to_message_id,
    thumb_url: thumbUrl,
    page_count: opts.pageCount,
  });
}

export async function sendLocationMessage(
  communityId: string,
  groupId: string,
  input: {
    label: string;
    latitude: number;
    longitude: number;
    reply_to_message_id?: string;
  }
): Promise<CommunityMessage> {
  return sendCommunityMessage(communityId, groupId, {
    message_type: 'location',
    content: input.label,
    reply_to_message_id: input.reply_to_message_id,
    media: {
      latitude: input.latitude,
      longitude: input.longitude,
      location_label: input.label,
    },
  });
}

export async function deleteMessage(
  communityId: string,
  groupId: string,
  messageId: string
): Promise<void> {
  await axiosInstance.delete(
    `/communities/${communityId}/groups/${groupId}/messages/${messageId}`
  );
}

export async function pinMessage(
  communityId: string,
  groupId: string,
  messageId: string
): Promise<{ message: CommunityMessage; is_pinned: boolean }> {
  const res = await axiosInstance.put<{ message: CommunityMessage; is_pinned: boolean }>(
    `/communities/${communityId}/groups/${groupId}/messages/${messageId}/pin`
  );
  return res.data;
}

export async function forwardMessages(
  communityId: string,
  groupId: string,
  payload: { target_community_id: string; message_ids: string[] }
): Promise<CommunityMessage[]> {
  const res = await axiosInstance.post<{ messages: CommunityMessage[] }>(
    `/communities/${communityId}/groups/${groupId}/messages/forward`,
    payload
  );
  return res.data.messages;
}

export async function markRead(
  communityId: string,
  groupId: string,
  upToSeq: number
): Promise<void> {
  await axiosInstance.patch(
    `/communities/${communityId}/groups/${groupId}/read`,
    { up_to_seq: upToSeq }
  );
}

// ---------------------------------------------------------------------------
// Invites
// ---------------------------------------------------------------------------

export async function createInvite(
  communityId: string,
  data: { group_id?: string; max_uses?: number; invite_audience?: 'admin' | 'app' } = {}
): Promise<CommunityInvite> {
  const res = await axiosInstance.post<{ invite: CommunityInvite }>(
    `/communities/${communityId}/invites`,
    data
  );
  return res.data.invite;
}

/** Dedupe concurrent redeem calls for the same token (StrictMode double-mount, double-click). */
const redeemInviteInflight = new Map<
  string,
  Promise<{ community_id: string; group_id: string | null }>
>();

export async function redeemInvite(
  token: string
): Promise<{ community_id: string; group_id: string | null }> {
  const existing = redeemInviteInflight.get(token);
  if (existing) return existing;

  const promise = axiosInstance
    .post<{ community_id: string; group_id: string | null }>(
      `/communities/invites/${token}/redeem`
    )
    .then((res) => res.data)
    .finally(() => {
      redeemInviteInflight.delete(token);
    });

  redeemInviteInflight.set(token, promise);
  return promise;
}

